import { RecentVisitedVehicle } from '@components/dealsheet/shared/sections/RecentWebsiteViews';
import blackbookImage from '../../assets/svgs/logo-blackbook.svg';
import carFaxImage from '../../assets/svgs/logo-carfax.svg';
import galvesImage from '../../assets/svgs/logo-galves.svg';
import bluebookImage from '../../assets/svgs/logo-kbb.svg';
import manheimImage from '../../assets/svgs/logo-manheim.svg';
import { BrandsService } from '@services/shared/BrandsService';

export const hasRole = (userPermissions: readonly string[], role: string): boolean => {
  return userPermissions.includes(role);
};

export const currencyFormat = (str: string | number, isReverse = false) => {
  if (!isNaN(parseFloat(str?.toString()))) {
    const num = Math.round(parseFloat(str?.toString()));
    const formattedNum = num.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    if (isReverse) {
      return num < 0
        ? `$${formattedNum}`
        : num === 0
          ? `$${formattedNum}`
          : `-$${Math.abs(num)
            .toFixed(0)
            .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
    }
    return num < 0
      ? `-$${Math.abs(num)
        .toFixed(0)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`
      : `$${formattedNum}`;
  }
  return '';
};

export const capitalize = (word: string = '') => word.charAt(0).toUpperCase() + word.slice(1);

export const capitalizeAll = (value: string = '') => {
  if (value !== null) {
    const splitString: Array<string> = value.toLowerCase().split(' ');
    splitString.forEach((val, i) => {
      splitString[i] = splitString[i].substring(0, 1).toUpperCase() + splitString[i].substring(1);
    });

    return splitString.join(' ');
  }

  return value;
};

export const getGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const findClosestGreaterValue = (arr: Array<number>, value: number): number => {
  const result = Math.max(...arr);
  const nextValue = arr.find((item) => item >= value);
  return nextValue ?? result;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return '';
};

export const formatEUIPhoneNumber = (phoneNumberString: string): string => {
  phoneNumberString = phoneNumberString.replace(/\s/g, '');
  const match = phoneNumberString.match(/^\(?(\d{3})\)?(\d{3})[-]?(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return phoneNumberString;
};

export const transformPhoneNumber = (tel: string): string => {
  if (!tel) {
    return '';
  }

  const value = tel.toString().trim().replace(/^\+/, '');

  if (value.match(/[^0-9]/)) {
    return tel;
  }

  let country, city, number;

  switch (value.length) {
    case 10: // +1PPP####### -> C (PPP) ###-####
      country = 1;
      city = value.slice(0, 3);
      number = value.slice(3);
      break;

    case 11: // +CPPP####### -> CCC (PP) ###-####
      country = value[0];
      city = value.slice(1, 4);
      number = value.slice(4);
      break;

    case 12: // +CCCPP####### -> CCC (PP) ###-####
      country = value.slice(0, 3);
      city = value.slice(3, 5);
      number = value.slice(5);
      break;

    default:
      return tel;
  }

  if (country === 1) {
    country = '';
  }

  number = number.slice(0, 3) + '-' + number.slice(3);

  return (country + ' (' + city + ') ' + number).trim();
};

export const extractPhoneNumber = (phoneNumer: string): string => {
  return phoneNumer.replace(/[^Z0-9]/g, '');
};

export const formatStringAsPerLocale = (input: string): string => {
  return !isNaN(Number(input))
    ? Number(input).toLocaleString(undefined, { maximumFractionDigits: 0 })
    : input;
};

export const getLoggedInFullName = (parsedToken: any): string => {
  return !parsedToken?.isShadowLogin
    ? parsedToken.firstName + ' ' + parsedToken.lastName
    : parsedToken.shadowUserFirstName + ' ' + parsedToken.shadowUserLastName;
};

export const getLoggedInUserName = (parsedToken: any): string => {
  return !parsedToken?.isShadowLogin
    ? parsedToken.userName
    : formatShadowUserName(parsedToken.userName, parsedToken.shadowUserName);
};

export const formatShadowUserName = (primaryUserName: string, shadowUserName: string): string => {
  const userName = shadowUserName.replace('@', '%20');
  return `${userName}/${primaryUserName}`;
};

export const truncate = (str: string, lengthToTrucate: number): string => {
  return str.length > 10 ? str.substring(0, lengthToTrucate) + '...' : str;
};

export const currencyFormatLoyalty = (str: string | number) => {
  if (!isNaN(parseInt(str?.toString()))) {
    const num = parseInt(str?.toString());
    return '$' + num?.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }
  return '';
};

export const currencyFormatEditMsrp = (str: string | number) => {
  if (!isNaN(parseInt(str?.toString()))) {
    const num = parseInt(str?.toString());
    return '' + num.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }
  return '';
};

export const hashCode = (str: string) => {
  let hash = 0,
    chr;
  const len = str.length;
  if (str.length === 0) {
    return hash;
  }
  for (let i = 0; i < len; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // convert to 32bit integer
  }
  return hash;
};

export const privateIncentiveDateFormat = (str: string | Date) => {
  const day = new Date(str).getUTCDate();
  const month = new Date(str).getUTCMonth();
  const year = new Date(str).getUTCFullYear();
  return addZero((month + 1).toString()) + '/' + addZero(day.toString()) + '/' + year;
};

export const addZero = (str: string) => {
  if (str.length <= 1) {
    str = '0' + str;
  }
  return str;
};

export const hiddenPhone = (phone: string) => {
  if (!phone) {
    return '';
  }
  return phone.replace(/\d/g, '•');
};

export const GalvesTradeBookProviderName = 'Galves';
export const ManheimTradeBookProviderName = 'Manheim';
export const BlackbookTradeBookProviderName = 'BlackBook';
export const KbbTradeBookProviderName = 'BlueBook';
export const CarfaxTradeBookProviderName = 'Carfax';

const providersImages = new Map<string, string>([
  [GalvesTradeBookProviderName, galvesImage],
  [ManheimTradeBookProviderName, manheimImage],
  [BlackbookTradeBookProviderName, blackbookImage],
  [KbbTradeBookProviderName, bluebookImage],
  [CarfaxTradeBookProviderName, carFaxImage],
]);

export const getTradeBookLogo = (tradeBookProviderName: string): string | undefined => {
  if (!tradeBookProviderName) {
    return null;
  }
  return providersImages.get(tradeBookProviderName) || undefined;
};

export const formatCurrency = (value, isReverse = false) => {
  if (value === undefined || isNaN(value) || value === 0) {
    return '$0';
  }
  const roundedValue = Math.round(value);
  const absValue = Math.abs(roundedValue);
  const formattedValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(absValue);
  if (!isReverse) {
    return roundedValue < 0 ? `-${formattedValue}` : formattedValue;
  } else if (isReverse) {
    return roundedValue < 0 ? formattedValue : `-${formattedValue}`;
  }
};

export const isNegative = (value) => {
  const roundedValue = Math.round(value);
  return roundedValue < 0;
};

export const toCamelCase = (str) => {
  const upperCaseBrands = ['Mini', 'Gmc', 'Infiniti', 'Bmw', 'Gm'];

  return str.toLowerCase().replace(/\b\w+/g, (word) => {
    return upperCaseBrands.includes(word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      ? word.toUpperCase()
      : word.charAt(0).toUpperCase() + word.slice(1);
  });
};

export const getValueForCash = (data) => {
  const tradeValue = data?.tradeEquityBreakdown?.tradeValue;
  const estimatedPayoff = data?.tradeEquityBreakdown?.estimatedPayoff;
  const moneyBack = data?.tradeEquityBreakdown?.moneyBack;
  const tradeEquity = tradeValue - estimatedPayoff;

  return tradeEquity;
};

export function getVehicleStatus(isNew: boolean, isCpo: boolean, isGhost: boolean): string {
  if (isNew) {
    return 'new';
  } else if (!isNew && isCpo && !isGhost) {
    return 'cpo';
  } else if (!isNew && !isCpo && !isGhost) {
    return 'po';
  } else if (!isNew && !isCpo && isGhost) {
    return 'sample';
  }
  return '';
}

export function isVehicleUsed(isNew: boolean, isCpo: boolean, isGhost: boolean): boolean {
  if (isNew) {
    return false;
  } else if (!isNew && isCpo && !isGhost) {
    return true;
  } else if (!isNew && !isCpo && !isGhost) {
    return true;
  } else if (!isNew && !isCpo && isGhost) {
    return false;
  }
  return false;
}

function checkEligibility(currentDealType: string, vehicle: any): boolean {
  switch (currentDealType.toLowerCase()) {
    case 'cash':
      return vehicle.isAvailableForReplacementCash;
    case 'finance':
      return vehicle.isAvailableForReplacementFinance;
    case 'lease':
      return vehicle.isAvailableForReplacementLease;
    default:
      return false;
  }
}
function isOffBrand(selectedVehicle: any, current): boolean {
  return selectedVehicle.brandId !== current.brandId;
}

function isCurrentOffBrand(currentDealerId): boolean {
  return currentDealerId && currentDealerId.startsWith('OB');
}

// function isSecondaryOnBrand(vehicle: any, currentDealerId): boolean {
//   //if vehicle.dealeId does not containe "OB" and it's not match with currentDealerId then it's secondary on brand
//   return vehicle.dealerId && !vehicle.dealerId.startsWith('OB') && vehicle.dealerId !== currentDealerId;
// }

export const isSetPrimaryEnabled = (
  vehicle,
  current,
  primaryReplacement,
  secondaryReplacement,
  turnReplacement,
  offerId,
  currentDealerId,
  currentDealType,
  permissions,
  isPrivateIncentiveApplied,
  isConquest,
): boolean => {
  if (
    offerId === '3' &&
    turnReplacement &&
    vehicle?.amVehicleId !== turnReplacement?.replacementAmVehicleId
  ) {
    return false;
  }
  if (isPrivateIncentiveApplied && !isConquest) {
    return false;
  }
  if (!permissions.includes('userCanSaveReplacementChromeId')) {
    return false;
  }
  if (
    (vehicle?.amVehicleId === primaryReplacement?.replacementAmVehicleId &&
      vehicle?.vin === primaryReplacement?.replacementVin) ||
    (vehicle?.amVehicleId === secondaryReplacement?.replacementAmVehicleId &&
      vehicle?.vin === secondaryReplacement?.replacementVin)
  ) {
    return false;
  }
  if (isCurrentOffBrand(currentDealerId)) {
    const vehicleStatus = getVehicleStatus(
      vehicle?.isNew || vehicle?.isVehicleNew,
      vehicle.isCpo,
      vehicle.isGhostVehicle,
    );
    switch (vehicleStatus) {
      case 'new':
        return false;
      case 'cpo':
        return currentDealType === 'lease' ? false : true;
      case 'po':
        return currentDealType === 'lease' ? false : true;
      case 'sample':
        return checkEligibility(currentDealType, vehicle);
      default:
        return false;
    }
  } else {
    const vehicleStatus = getVehicleStatus(
      vehicle?.isNew || vehicle?.isVehicleNew,
      vehicle.isCpo,
      vehicle.isGhostVehicle,
    );
    switch (vehicleStatus) {
      case 'new':
        return false;
      case 'cpo':
        return currentDealType === 'lease' ? false : true;
      case 'po':
        return currentDealType === 'lease' ? false : true;
      case 'sample':
        return isOffBrand(vehicle, current) ? false : checkEligibility(currentDealType, vehicle);
      default:
        return false;
    }
  }
};

export const formatDate = (date: string): string => {
  const parsedDate = new Date(date);
  const month = (parsedDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = parsedDate.getUTCDate().toString().padStart(2, '0');
  const year = parsedDate.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

export const hiddenField = (field: string) => {
  if (!field) {
    return '';
  }
  if (typeof field !== 'string') {
    return '';
  }
  return field?.replace(/./g, '•');
};

export const convertStringToNumber = (input: any, isDecimal = false): number => {
  if (
    input === null ||
    input === undefined ||
    input === '' ||
    input === '0' ||
    input === '0.0' ||
    input === '0.00' ||
    input === 'Infinity'
  ) {
    return 0;
  }

  // If input is already a number, return it
  if (typeof input === 'number') {
    return input;
  }

  // If input is not provided, return 0
  if (!input) {
    return 0;
  }

  // Remove commas from the input string
  let sanitizedInput = input.replace(/,/g, '');

  // attach 0 after . if senitized input is like "20." then convert it to "20.0". if it's "2.5" then kept as it is
  if (isDecimal && sanitizedInput.includes('.')) {
    if (sanitizedInput.split('.')[1] === '') {
      sanitizedInput = sanitizedInput + '0';
    }
  }

  // Try to parse the sanitized input as a float
  let parsedNumber;
  if (isDecimal && sanitizedInput.includes('.')) {
    parsedNumber = parseFloat(sanitizedInput).toFixed(1);
  } else {
    parsedNumber = parseFloat(sanitizedInput);
  }

  // Check if the parsed number is a valid number
  if (isNaN(parsedNumber)) {
    return 0;
  }

  //  check if parsedNumber is nagative, if yes then convert to positive value
  if (parsedNumber < 0) {
    parsedNumber = Math.abs(parsedNumber);
  }

  return parsedNumber;
};
/**
 * Formats a date string into a relative time string (e.g., "5 minutes ago", "2 months ago").
 * Handles null/undefined/invalid inputs and provides approximate time differences.
 * Includes logic to round up to the next month if the remaining days are 15 or more.
 *
 * @param dateString - The date string to format. Can be any format parsable by `new Date()`.
 * @returns A string representing the relative time, or 'N/A'/'Invalid Date' for invalid inputs.
 */
export const formatRelativeTimeEnhanced = (dateString: string | undefined | null): string => {
  // Return 'N/A' if the input date string is null, undefined, or empty.
  if (!dateString) {
    return 'N/A';
  }

  try {
    // Attempt to parse the input string into a Date object.
    const date = new Date(dateString);
    // Get the current time.
    const now = new Date();
    // Calculate the difference in milliseconds between now and the input date.
    const diffInMs = now.getTime() - date.getTime();

    // Check if the parsed date was valid. `getTime()` returns NaN for invalid dates.
    if (isNaN(diffInMs)) {
      return 'Invalid Date';
    }

    // If the date is in the future or exactly now, display 'Just now'.
    if (diffInMs <= 0) {
      return 'Just now';
    }

    // Calculate total seconds, minutes, and hours from the millisecond difference.
    const seconds = Math.floor(diffInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    // Calculate the raw difference in days, including fractions.
    const daysRaw = diffInMs / (1000 * 60 * 60 * 24);

    // Define constants for average lengths used in calculations.
    const averageDaysInMonth = 30.44; // Approximate average days in a month (365.25 / 12)
    const averageDaysInYear = 365.25; // Approximate average days in a year (accounts for leap years)
    // Define the threshold in days to round up the month count.
    const roundingThresholdDays = 15;

    // Calculate the number of full years that have passed.
    const years = Math.floor(daysRaw / averageDaysInYear);

    // Calculate months only if the difference is less than a year.
    // This variable will hold the final month count, potentially rounded up.
    let months = 0;
    if (years === 0) {
      // Calculate the number of full months passed (rounded down).
      const wholeMonths = Math.floor(daysRaw / averageDaysInMonth);
      // Calculate the remaining days after accounting for the full months.
      const remainingDays = daysRaw - wholeMonths * averageDaysInMonth;

      // Apply rounding: if remaining days meet the threshold, increment month count.
      if (remainingDays >= roundingThresholdDays) {
        months = wholeMonths + 1;
      } else {
        months = wholeMonths;
      }
      // Note: If rounding results in 12 months, the 'years > 0' check below
      // will correctly handle it as 1 year.
    }

    // Determine the output string based on the largest relevant time unit.
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''} ago`;
    } else if (months > 0) {
      // Uses the potentially rounded-up month value calculated above.
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else if (daysRaw >= 1) {
      // If less than a month but at least 1 day, show days.
      // Use floor to get whole days.
      const daysFloor = Math.floor(daysRaw);
      return `${daysFloor} day${daysFloor > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      // If less than a day but at least 1 hour, show hours.
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      // If less than an hour but at least 1 minute, show minutes.
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      // If less than a minute, show 'Just now'.
      return 'Just now';
    }
  } catch (error) {
    // Log any unexpected errors during execution.
    console.error('Error formatting date:', dateString, error);
    // Return 'Invalid Date' if an error occurred.
    return 'Invalid Date';
  }
};

export const getVehicleName = (vehicle: RecentVisitedVehicle): string => {
  const year = vehicle.year?.toString() || '';
  const make = BrandsService.getByBrandId(vehicle?.brandId) || '';
  const model = vehicle.model || '';
  const trim = vehicle.trim || '';

  // Return "Vehicle" if only year is present or if all core information is missing
  if ((!year && !make && !model && !trim) || (year && !make && !model && !trim)) {
    return 'Vehicle';
  }

  let modelToDisplay = model;
  let trimToDisplay = trim;

  // If model is part of the trim (e.g., Model="Camry", Trim="Camry LE"),
  // don't display model separately to avoid "Year Make Camry Camry LE".
  // trimToDisplay will still hold the full trim (e.g., "Camry LE").
  if (model && trim && trim.trim().toLowerCase().startsWith(model.trim().toLowerCase())) {
    modelToDisplay = '';
  }

  const nameParts = [year, make, modelToDisplay, trimToDisplay]
    .map(part => part.trim())
    .filter(Boolean);

  return nameParts.join(' ');
};

export const getWebsiteActivityVehicleName = (vehicle: RecentVisitedVehicle): string | null => {
  const year = vehicle.year?.toString() || '';
  const make = BrandsService.getByBrandId(vehicle?.brandId) || '';
  const model = vehicle.model || '';
  const trim = vehicle.trim || '';

  // Return null if only year is present or if all core information is missing
  if ((!year && !make && !model && !trim) || (year && !make && !model && !trim)) {
    return null;
  }

  let modelToDisplay = model;
  let trimToDisplay = trim;

  // If model is part of the trim (e.g., Model="Camry", Trim="Camry LE"),
  // don't display model separately to avoid "Year Make Camry Camry LE".
  // trimToDisplay will still hold the full trim (e.g., "Camry LE").
  if (model && trim && trim.trim().toLowerCase().startsWith(model.trim().toLowerCase())) {
    modelToDisplay = '';
  }

  const nameParts = [year, make, modelToDisplay, trimToDisplay]
    .map(part => part.trim())
    .filter(Boolean);

  return nameParts.join(' ');
};
